import { _ } from "../../../core/polyglot/polyglot.js";
import { currentPeriodType } from "../../../models/period/PeriodType.js";
import { download } from "../../../core/helpers/uri-util.js";
import { makeCancelAndOkFooterBuilder } from "../../../core/widgets/dialogs/footers/footers.js";
import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import {
	notBlank,
	validateAll,
} from "../../../core/widgets/forms/validators.js";
import accountingDocument from "../../shared/accountingDocument.js";
import AlertCollector from "../../../core/alerts/AlertCollector.js";
import ChartOfAccounts from "../../../models/chartOfAccounts/ChartOfAccounts.js";
import dialog from "../../../core/widgets/dialog.js";
import FormulaTokenActionsRenderer from "./visitors/FormulaTokenActionsRenderer.js";
import FormulaTokenFormBuilder from "./visitors/FormulaTokenFormBuilder.js";
import FormulaTokenFormCSSClassBuilder from "./visitors/FormulaTokenFormCSSClassBuilder.js";
import getKeyFigureService from "../../../core/services/getKeyFigureService.js";
import InlineEditorWidget from "../../../core/widgets/InlineEditorWidget/InlineEditorWidget.js";
import KeyFigure from "../../../models/keyFigure/KeyFigure.js";
import KeyFigureActionsWidget from "./KeyFigureActionsWidget.js";
import KeyFigureCalculationService from "./KeyFigureCalculationService.js";
import KeyFigureEditorWidget from "./KeyFigureEditorWidget.js";
import KeyFigurePresetService from "../../../core/services/KeyFigurePresetService.js";
import session from "../../../core/session.js";
import TitleWidget from "../../../core/chrome/TitleWidget.js";
import visibleKeyFigures from "../../../models/keyFigure/preset/visibleKeyFigures.js";

/**
 * Let the user create and edit key figures.
 */
const keyFigureEditorDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		my.keyFigureService = getKeyFigureService();
		my.keyFigure = new KeyFigure();
		my.defaultTitle = _("Custom KPI");

		my.keyFigureCalculationService = new KeyFigureCalculationService({
			companyId: session.getCompanyId(),
		});

		my.isBimestersEnabled = false;

		my.chartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts/#id",
			model: ChartOfAccounts,
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
				id: session.getCurrentChartOfAccountsId(),
			},
			antiForgeryToken: session.getAntiForgeryToken(),
		});

		my.keyFigurePresetService = new KeyFigurePresetService({
			companyId: session.getCompanyId(),
			keyFigureService: getKeyFigureService(),
		});

		that.documentId = "kpi-editor";

		that.title = (html) => {
			html.render(my.keyFigure.getName() || _("Custom KPI"));
		};

		that.subtitle = (html) => {
			html.render(my.keyFigure.getSubtitle() || _("KPI description…"));
		};
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.titleEditor = new InlineEditorWidget({
			getContent: () => my.keyFigure.getName(),
			placeholder: my.defaultTitle,
		});

		my.titleEditor.contentChanged.register((name) =>
			my.keyFigure.setName(name),
		);

		my.subtitleEditor = new InlineEditorWidget({
			getContent: () => my.keyFigure.getSubtitle(),
			placeholder: _("KPI description…"),
		});

		my.subtitleEditor.contentChanged.register((subtitle) =>
			my.keyFigure.setSubtitle(subtitle),
		);

		my.titleWidget = new TitleWidget({
			title: my.titleEditor,
			subtitle: my.subtitleEditor,
		});

		my.keyFigureEditor = new KeyFigureEditorWidget({
			keyFigure: my.keyFigure,
			dimensionTree: session.getDimensionTree(),
		});

		my.keyFigureEditor.keyFigureChanged.register(my.updateKeyFigureResult);

		my.keyFigureEditor.tokenSelected.register(my.updateActions);

		my.keyFigureActions = new KeyFigureActionsWidget({
			keyFigures: my.keyFigures,
			keyFigure: my.keyFigure,
			currentCompany: session.getCompany(),
			chartOfAccounts: my.chartOfAccounts,
			isAccountGroupsUsageInKpiEditorEnabled:
				session.agency.isAccountGroupsUsageInKpiEditorEnabled,
		});

		my.keyFigureActions.saveRequested.register(() =>
			my.saveKeyFigure(my.keyFigure),
		);

		my.keyFigureActions.saveAsRequested.register(my.saveAsKeyFigure);

		my.keyFigureActions.removeRequested.register(my.deleteKeyFigure);

		my.keyFigureActions.addTokenRequested.register(my.showCreateItemDialog);

		my.keyFigureActions.downloadRequested.register(my.downloadKeyFigure);
	};

	that.getVisibleKeyFigures = function () {
		return my.visibleKeyFigures;
	};

	that.updateActions = function () {
		my.updateActions();
	};

	that.periodChanged = function () {
		my.updateKeyFigureResult();
	};

	that.show = function (keyFigureId, isBimestersEnabled) {
		my.isBimestersEnabled = isBimestersEnabled;
		that.showUpdating();
		my.keyFigureId = keyFigureId;

		Promise.all([
			my.keyFigureService.getAllFromCache(), // It works only because we populate the cache in app.js
			my.keyFigurePresetService.getVisibleKeyFigures(),
			my.chartOfAccountsRepository.get(),
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
		]).then(([allKeyFigures, visibleKeyFigures, chartOfAccounts]) => {
			my.keyFigures = allKeyFigures;
			my.keyFigureActions.setKeyFigures(my.keyFigures);
			my.visibleKeyFigures = visibleKeyFigures;
			my.chartOfAccounts = chartOfAccounts;

			if (keyFigureId) {
				// eslint-disable-next-line promise/no-nesting -- We should move this code to a service
				my.keyFigureService.get(keyFigureId).then((keyFigure) => {
					my.setKeyFigure(keyFigure);
					that.showContent();
				});
			} else {
				that.showContent();
			}
		});
	};

	that.actions = function () {
		return [my.keyFigureActions, my.selectedTokenActionsRenderer()];
	};

	that.hasPeriodPicker = function () {
		return true;
	};

	that.hasActionsMenu = function () {
		return true;
	};

	that.tokenEdited = function () {
		my.keyFigureEditor.updateFormula();
	};

	that.deleteFormulaToken = function (token) {
		my.keyFigureEditor.deleteFormulaToken(token);
	};

	that.showItemEditionDialog = function ({
		formulaToken,
		callback = () => {},
	}) {
		const builder = new FormulaTokenFormBuilder({
			keyFigures: my.keyFigures,
			chartOfAccounts: my.chartOfAccounts,
			company: session.getCompany(),
			period: session.getPeriod(),
			editedKeyFigureId: my.keyFigure.getId(),
		});

		const form = builder.visit(formulaToken);
		const cssClass = new FormulaTokenFormCSSClassBuilder().visit(
			formulaToken,
		);

		if (form.isEmpty()) {
			callback(formulaToken);
			return;
		}

		const formDialog = dialog({
			header: formulaToken.getLabel(),
			cssClass,
			body: form,
			footer: (html) => {
				html.render(
					makeCancelAndOkFooterBuilder({
						okLabel: _("Add"),
						callback: () => {
							form.submit();
							if (!form.invalidFields().length) {
								formDialog.hide();
								callback(formulaToken);
							}
						},
					})({ hide: formDialog.hide }),
				);
			},
		});

		formDialog.show();
	};

	my.renderContentOn = function (html) {
		html.render(my.keyFigureEditor);
	};

	my.selectedTokenActionsRenderer = function () {
		const selectedToken = my.getSelectedFormulaToken();

		return (html) => {
			if (!selectedToken) {
				return;
			}

			html.div(
				{ klass: "right-menu-section" },
				html.label(
					_("{token} settings", {
						token: selectedToken.getLabel(),
					}),
				),
				my.renderSelectedTokenActionsOn,
			);
		};
	};

	my.renderSelectedTokenActionsOn = function (html) {
		const selectedToken = my.getSelectedFormulaToken();
		const actionsRenderer = new FormulaTokenActionsRenderer({
			isBimestersEnabled: my.isBimestersEnabled,
			dimensionTree: session.getDimensionTree(),
			currentPeriodTypeId: currentPeriodType.getId(),
		});

		html.render(actionsRenderer.tokenActionsRenderer(selectedToken)(that));
	};

	my.createKeyFigure = function (keyFigure) {
		return (
			my.keyFigureService
				.create(keyFigure, { notify: false })
				// eslint-disable-next-line no-shadow -- bad practice, we should fix this
				.then((keyFigure) => my.setKeyFigure(keyFigure))
				.then(() => {
					my.addToVisibleKeyFigures(my.keyFigure);
				})
				.then(() => my.success(_("New key figure saved")))
				.then(() =>
					my.redirectTo("kpi-editor", {
						keyFigureId: my.keyFigure.getId(),
					}),
				)
		);
	};

	my.saveKeyFigure = function (keyFigure) {
		if (!my.canSaveKeyFigure(keyFigure)) {
			return;
		}

		if (keyFigure.isNew()) {
			my.createKeyFigure(keyFigure);
		} else {
			my.keyFigureService
				.update(keyFigure)
				// eslint-disable-next-line no-shadow -- bad practice, we should fix this
				.then((keyFigure) => my.setKeyFigure(keyFigure));
		}
	};

	my.validateName = function (name) {
		if (name !== my.keyFigure.getName()) {
			return;
		}
		throw _(
			"The name of the copy cannot be the same as the name of the original KPI",
		);
	};

	my.saveAsKeyFigure = function () {
		const copy = my.keyFigure.clone();
		copy.setName(`${my.keyFigure.getName()} copy`);

		my.configure({
			title: _("Save as..."),
			body: _(
				"You are about to save a copy of the KPI. Please choose a name for it.",
			),
			formSpec: {
				saveAction: () => my.saveKeyFigure(copy),
				model: copy,
			},
			formBuilder: (form) => {
				form.input({
					label: _("Name"),
					name: "name",
					accessor: () => copy.getName(),
					mutator: (name) => copy.setName(name),
					validator: validateAll(notBlank(), my.validateName),
				});
			},
		});
	};

	my.deleteKeyFigure = function () {
		my.keyFigureService.remove(my.keyFigure).then((nullOrBaseKeyFigure) => {
			if (nullOrBaseKeyFigure) {
				my.setKeyFigure(nullOrBaseKeyFigure);
				that.update();
			} else {
				my.redirectTo("company-settings");
			}
		});
	};

	my.addToVisibleKeyFigures = function () {
		visibleKeyFigures.setKeyFigures([
			...visibleKeyFigures.getKeyFigures(),
			my.keyFigure,
		]);

		return my.keyFigurePresetService.update(visibleKeyFigures, {
			notify: false,
		});
	};

	my.setKeyFigure = function (keyFigure) {
		my.keyFigure = keyFigure;

		my.keyFigureEditor.setKeyFigure(keyFigure, {
			getAccountName: (id) => my.chartOfAccounts.getAccountName(id),
		});

		my.keyFigureActions.setKeyFigure(keyFigure);
		my.updateActions();
		my.updateKeyFigureResult();
	};

	my.getSelectedFormulaToken = function () {
		return my.keyFigureEditor.getSelectedFormulaToken();
	};

	my.updateKeyFigureResult = function () {
		const lastPeriods = session
			.getCompany()
			.getLatestPeriods(12, session.getPeriod());

		my.keyFigureCalculationService
			.fetchKeyFigureResult({
				periods: lastPeriods,
				keyFigure: my.keyFigure,
			})
			.then((keyFigureResult) =>
				my.keyFigureEditor.setKeyFigureResult(keyFigureResult),
			);
	};

	my.addFormulaToken = function (token) {
		my.keyFigureEditor.addFormulaToken(token);
	};

	my.showCreateItemDialog = function (formulaToken) {
		that.showItemEditionDialog({
			formulaToken,
			callback: my.addFormulaToken,
		});
	};

	my.canSaveKeyFigure = function (keyFigure) {
		const collector = my.validate(keyFigure);

		if (!collector.hasError()) {
			return true;
		}

		collector
			.getErrors()
			.forEach((error) => my.error(error.getDescription()));

		return false;
	};

	my.validate = function (keyFigure) {
		const collector = new AlertCollector();
		keyFigure.validate({ collector });
		my.keyFigureEditor.validate({ collector });

		return collector;
	};

	my.downloadKeyFigure = function () {
		download({
			data: JSON.stringify(my.keyFigure.serialize()),
			filename: `${my.keyFigure.getName()}.json`,
			mimetype: "application/json",
		});
	};
});

export default keyFigureEditorDocument;
